import React, { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { Button, Checkbox, InputText, Label } from '@teo/components';
import { CloseCircle, TaskCheckmark, EyeOff, Eye } from '@teo/components/icons';
import { Link, useNavigate } from 'react-router-dom';
import { register } from '@/query/auth';
import { emailRegex, passwordRegex } from '@/constants/regex.constants';
import { AxiosError, AxiosResponse } from 'axios';
import { ErrorBox } from '@/components/ErrorBox';
import i18n from 'i18next';
import { axios } from '@/axios';
import { config } from '@/config';
import { authActions } from '@/actions/auth';
import SocialLoginButton from '@/components/Auth/SocialLoginButton/SocialLoginButton';
import {
  availableLoginMethods,
  loginMethodLogo,
  OrganisationLoginInfo,
} from './loginMethods';

function AuthRegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToConditions, setAgreedToConditions] = useState(false);
  const [mailingConfirmation, setMailingConfirmation] = useState('');
  const [hasAuthError, setAuthError] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [emailWasTouched, setEmailWasTouched] = useState(false);
  const [confirmPasswordWasTouched, setConfirmPasswordWasTouched] =
    useState(false);

  const [validLength, setValidLength] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [upperCase, setUpperCase] = useState(false);
  const [lowerCase, setLowerCase] = useState(false);
  const [requiredLength] = useState(8);
  const [validEmail, setValidEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
  const [organisationInfo, setOrganisationInfo] =
    useState<OrganisationLoginInfo | null>(null);
  const [methodsLoading, setMethodsLoading] = useState(
    Boolean(
      localStorage.getItem('invite') ||
        new URLSearchParams(window.location.search).get('organisation')
    )
  );
  const [invitationError, setInvitationError] = useState(false);
  const inviteToken = localStorage.getItem('invite');
  const organisationId = new URLSearchParams(window.location.search).get(
    'organisation'
  );
  const { useLocalLogin, loginMethods } =
    availableLoginMethods(organisationInfo);

  useEffect(() => {
    let cancelled = false;
    const loadRegistrationInfo = async () => {
      try {
        if (inviteToken) {
          // The auth axios instance types the whole response, not the body.
          const response = await axios.get<
            AxiosResponse<{
              firstName: string;
              lastName: string;
              email: string;
              organisation: OrganisationLoginInfo | null;
            }>
          >(`${config.backend}/invite/registration-info`, {
            params: { token: inviteToken, organisation: organisationId },
            skipAuthRedirect: true,
          });
          if (cancelled) return;
          setFirstname(response.data.firstName || '');
          setLastname(response.data.lastName || '');
          setEmail(response.data.email || '');
          setOrganisationInfo(response.data.organisation);
        } else if (organisationId) {
          try {
            const response = await axios.get<
              AxiosResponse<OrganisationLoginInfo>
            >(
              `${config.backend}/organisationinfo/${encodeURIComponent(
                organisationId
              )}`,
              { skipAuthRedirect: true }
            );
            if (!cancelled) setOrganisationInfo(response.data);
          } catch {
            // Like the login page: an unknown organisation or a failed lookup
            // falls back to the default login methods instead of blocking
            // registration.
            if (!cancelled) setOrganisationInfo(null);
          }
        }
      } catch (error) {
        if (cancelled) return;
        if ((error as AxiosError)?.response?.status === 404) {
          // An old invite must not prevent someone from registering normally.
          localStorage.removeItem('invite');
          setOrganisationInfo(null);
          setInvitationError(false);
        } else {
          setInvitationError(true);
        }
      } finally {
        if (!cancelled) setMethodsLoading(false);
      }
    };
    loadRegistrationInfo();
    return () => {
      cancelled = true;
    };
  }, [inviteToken, organisationId]);

  useEffect(() => {
    setValidLength(password.length >= requiredLength);
    setUpperCase(password.toLowerCase() !== password);
    setLowerCase(password.toUpperCase() !== password);
    setHasNumber(/\d/.test(password));
    setValidEmail(emailRegex.test(email));
  }, [password, requiredLength, email]);

  const basePath = window.location.pathname.slice(
    0,
    window.location.pathname.lastIndexOf('/')
  );

  const backToTop = () => {
    (document.getElementById('userForm') as HTMLFormElement).scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  function registerUser() {
    if (methodsLoading || invitationError || !useLocalLogin) return;
    if (!agreedToConditions) {
      setAuthError(true);
      backToTop();
      setErrorText(t('pages.auth.register.agreedToConditionsError'));
    } else if (
      !(
        passwordRegex.test(password) &&
        validLength &&
        hasNumber &&
        upperCase &&
        lowerCase &&
        requiredLength
      ) ||
      !validEmail ||
      email === '' ||
      password === '' ||
      confirmPassword === ''
    ) {
      setErrorText(t('pages.auth.register.password_and_email_ErrorMassage'));
    } else if (confirmPassword !== password) {
      setAuthError(true);
      backToTop();
      setErrorText(t('pages.auth.register.password_mismatch'));
    } else {
      register({
        firstName: firstname,
        lastName: lastname,
        username: `${firstname} ${lastname}`,
        email: email,
        password: password,
        repassword: confirmPassword,
        mailingConfirmation: mailingConfirmation,
        agreedToConditions: agreedToConditions,
        lang: i18n.language,
      })
        .then(() => {
          navigate(`${basePath}/register-success`, {
            state: { email: email.trim().toLowerCase() },
          });
        })
        .catch((e: AxiosError<{ message: string }>) => {
          setAuthError(true);

          const message = e?.response?.data?.message || e.toString();
          switch (message) {
            case 'User with this email already exists':
              setErrorText(t('pages.auth.register.registrationErrorMassage'));
              break;
            default:
              setErrorText(message);
              break;
          }

          console.error('Error', e);
          console.error('redirecting');
        });
    }
  }

  return (
    <div className="h-full overflow-hidden md:flex">
      <div
        className="flex h-full overflow-hidden md:w-1/2 md:items-center md:overflow-auto"
        id="userForm"
      >
        <form
          className="m-auto flex w-full max-w-[420px] flex-col max-md:h-full max-md:overflow-hidden"
          onSubmit={(event) => {
            event.preventDefault();
            registerUser();
          }}
        >
          <div className="space-y-6 px-5 pt-8 max-md:flex-grow max-md:overflow-auto md:space-y-8">
            <img
              className="h-12 w-12 md:h-16 md:w-16"
              src="/TEO_logo.svg"
              alt="Logo"
            />

            <h1 className="text-3xl-semibold">
              {t('pages.auth.register.title')}
            </h1>

            {!methodsLoading && invitationError && (
              <ErrorBox text={t('pages.auth.register.invalid_invitation')} />
            )}

            {!methodsLoading && !invitationError && !useLocalLogin && (
              <p className="text-sm text-grey-05">
                {t(
                  loginMethods.length
                    ? 'pages.auth.register.organisation_sign_in'
                    : 'pages.auth.register.no_login_methods'
                )}
              </p>
            )}

            {!methodsLoading && !invitationError && loginMethods.length > 0 && (
              <div className="flex space-x-3">
                {loginMethods.map((method) => (
                  <SocialLoginButton
                    key={method}
                    img={loginMethodLogo(organisationInfo, method)}
                    loginMethodName={method}
                    onClick={() =>
                      authActions.login(
                        method,
                        `/${i18n.language || 'nl'}/learn`
                      )
                    }
                  />
                ))}
              </div>
            )}

            {!methodsLoading && !invitationError && useLocalLogin && (
              <div className="space-y-6 md:space-y-8">
                {hasAuthError && <ErrorBox text={errorText} />}

                <p className="text-sm text-grey-05">
                  {t('pages.auth.register.subtitle')}
                </p>

                <div>
                  <Label htmlFor="firstname">
                    {t('pages.auth.register.firstName_label')}
                  </Label>
                  <InputText
                    id="firstname"
                    value={firstname}
                    onChange={(event) => {
                      setFirstname(event.target.value);
                    }}
                    placeholder={t('pages.auth.register.firstName_placeholder')}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="lastname">
                    {t('pages.auth.register.lastname_label')}
                  </Label>
                  <InputText
                    id="lastname"
                    value={lastname}
                    onChange={(event) => {
                      setLastname(event.target.value);
                    }}
                    placeholder={t('pages.auth.register.lastname_placeholder')}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="email">
                    {t('pages.auth.login.email_label')}*
                  </Label>
                  <InputText
                    id="email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                    }}
                    placeholder={t('pages.auth.register.email_placeholder')}
                    required
                    onBlur={(e) => {
                      if (!validEmail || e.target.value === '') {
                        setEmailWasTouched(true);
                      } else {
                        setEmailWasTouched(false);
                      }
                    }}
                    hasError={emailWasTouched}
                  />
                </div>

                <div>
                  <Label htmlFor="password">
                    {t('pages.auth.register.password_label')}
                  </Label>
                  <div className="relative space-y-2 text-grey-08">
                    <button
                      type="button"
                      tabIndex={-1}
                      className="absolute right-[7px] top-[13px] z-10 rounded-md p-1 text-grey-09 hover:bg-grey-transparent-01"
                      onClick={() => {
                        if (!showPassword) {
                          setShowPassword(true);
                        } else {
                          setShowPassword(false);
                        }
                      }}
                    >
                      {showPassword ? (
                        <Eye className="h-5 w-5" />
                      ) : (
                        <EyeOff className="h-5 w-5" />
                      )}
                    </button>
                    <input
                      id="password"
                      onChange={(event) => {
                        setPassword(event.target.value);
                      }}
                      placeholder={t(
                        'pages.auth.register.password_placeholder'
                      )}
                      type={showPassword ? 'text' : 'password'}
                      required
                      className="h-10 w-full rounded-md border border-solid border-transparent px-3 py-0 text-base text-grey-08 !shadow-none outline outline-2 !outline-offset-0 outline-transparent !ring-0 placeholder:text-sm placeholder:text-grey-04 enabled:bg-grey-transparent-01 enabled:hover:border-grey-transparent-03 enabled:hover:bg-white enabled:hover:outline-grey-transparent-01 enabled:focus:border-secondary-04 enabled:focus:bg-white enabled:focus:outline-secondary-01 enabled:active:border-secondary-04 enabled:active:bg-white enabled:active:outline-secondary-01 disabled:border-grey-03 disabled:bg-grey-transparent-02"
                    />
                  </div>

                  <div className="pt-3 pb-5 text-xs text-grey-07">
                    {t('pages.auth.register.password_must')}
                    <div className="mt-2 flex list-none flex-col">
                      <div className="flex items-center pt-2">
                        {!validLength || !requiredLength ? (
                          <CloseCircle
                            aria-hidden="true"
                            className="m-1 h-5 w-5 text-tint-red-dark"
                          />
                        ) : (
                          <TaskCheckmark
                            aria-hidden="true"
                            className="m-1 h-5 w-5 text-success-05"
                          />
                        )}
                        <span>{t('pages.auth.register.characters_long')}</span>
                      </div>
                      <div className="flex items-center pt-2">
                        {!lowerCase ? (
                          <CloseCircle
                            aria-hidden="true"
                            className="m-1 h-5 w-5 text-tint-red-dark"
                          />
                        ) : (
                          <TaskCheckmark
                            aria-hidden="true"
                            className="m-1 h-5 w-5 text-success-05"
                          />
                        )}
                        <span>{t('pages.auth.register.lowercase_letter')}</span>
                      </div>
                      <div className="flex items-center pt-2">
                        {!upperCase ? (
                          <CloseCircle
                            aria-hidden="true"
                            className="m-1 h-5 w-5 text-tint-red-dark"
                          />
                        ) : (
                          <TaskCheckmark
                            aria-hidden="true"
                            className="m-1 h-5 w-5 text-success-05"
                          />
                        )}
                        <span>{t('pages.auth.register.capital_letter')}</span>
                      </div>
                      <div className="flex items-center pt-2">
                        {!hasNumber ? (
                          <CloseCircle
                            aria-hidden="true"
                            className="m-1 h-5 w-5 text-tint-red-dark"
                          />
                        ) : (
                          <TaskCheckmark
                            aria-hidden="true"
                            className="m-1 h-5 w-5 text-success-05"
                          />
                        )}
                        <span>{t('pages.auth.register.digit_number')}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword">
                      {t('pages.auth.register.confirm_password_label')}
                    </Label>
                    <div className="relative space-y-2 text-grey-08">
                      <button
                        type="button"
                        tabIndex={-1}
                        className="absolute right-[7px] top-[6px] z-10 rounded-md p-1 text-grey-09 hover:bg-grey-transparent-01"
                        onClick={() => {
                          if (!showPasswordRepeat) {
                            setShowPasswordRepeat(true);
                          } else {
                            setShowPasswordRepeat(false);
                          }
                        }}
                      >
                        {showPasswordRepeat ? (
                          <Eye className="h-5 w-5" />
                        ) : (
                          <EyeOff className="h-5 w-5" />
                        )}
                      </button>
                      <InputText
                        id="confirmPassword"
                        onChange={(event) => {
                          setConfirmPassword(event.target.value);
                        }}
                        placeholder={t(
                          'pages.auth.register.confirm_password_placeholder'
                        )}
                        type={showPasswordRepeat ? 'text' : 'password'}
                        required
                        onBlur={(e) => {
                          if (
                            confirmPassword !== password ||
                            e.target.value === ''
                          ) {
                            setConfirmPasswordWasTouched(true);
                            setErrorText(
                              t('pages.auth.register.password_mismatch')
                            );
                          } else {
                            setConfirmPasswordWasTouched(false);
                          }
                        }}
                        hasError={confirmPasswordWasTouched}
                      />
                    </div>
                  </div>

                  <div>
                    <p className="mt-5 mb-2 text-sm">
                      {t('pages.auth.register.mailing_confirmation')}
                    </p>

                    <div className="pb-2">
                      <Checkbox
                        id="mailingConfirmationYes"
                        ariaLabel="test"
                        onChange={() => {
                          setMailingConfirmation('Yes');
                        }}
                        checked={mailingConfirmation === 'Yes'}
                      />

                      <label
                        htmlFor="mailingConfirmationYes"
                        className="pl-2.5 text-sm text-grey-08"
                      >
                        {t('pages.auth.register.yes')}
                      </label>
                    </div>

                    <div>
                      <Checkbox
                        id="mailingConfirmationNo"
                        ariaLabel="test"
                        onChange={() => {
                          setMailingConfirmation('No');
                        }}
                        checked={mailingConfirmation === 'No'}
                      />

                      <label
                        htmlFor="mailingConfirmationNo"
                        className="pl-2.5 text-sm text-grey-08"
                      >
                        {t('pages.auth.register.no')}
                      </label>
                    </div>
                  </div>

                  <div>
                    <p className="pt-4 pb-2 text-sm">
                      {t('pages.auth.register.agreement_title')}
                    </p>

                    <div className="flex items-center">
                      <Checkbox
                        id="agreedToConditions"
                        ariaLabel="test"
                        onChange={() =>
                          setAgreedToConditions(!agreedToConditions)
                        }
                        checked={agreedToConditions}
                      />

                      <label
                        htmlFor="agreedToConditions"
                        className="pl-2.5 text-sm text-grey-08"
                      >
                        {t('pages.auth.register.agreement_text')}
                        <Link
                          className="inline-block pl-2 pr-2 text-sm text-tint-blue hover:underline"
                          tabIndex={-1}
                          to="../privacy"
                        >
                          {t('pages.auth.register.privacy')}
                        </Link>
                        {t('pages.auth.register.agreement_text2')}
                        <Link
                          className="inline-block text-sm text-tint-blue hover:underline"
                          tabIndex={-1}
                          to="../voorwaarden"
                        >
                          {t('pages.auth.register.terms_of_use')}
                        </Link>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex px-5 py-8 max-md:flex max-md:flex-col max-md:space-y-3 md:grid md:grid-cols-2 md:gap-4">
            {!methodsLoading && !invitationError && useLocalLogin && (
              <Button type="submit">
                {t('pages.auth.register.register_button')}
              </Button>
            )}

            <Button onClick={() => navigate('../login')} variant="outline">
              {t('pages.auth.register.login_button')}
            </Button>
          </div>
        </form>
      </div>

      <div className="w-1/2 max-md:hidden">
        <img
          className="h-full w-full object-cover"
          role="presentation"
          src="/images/interface/auth/login.jpg"
          alt="login_image"
        />
      </div>
    </div>
  );
}

export default AuthRegisterPage;
